import { minBy } from 'lib/helpers';

import { IMAGE_AEM_CLOUD_PREFIX } from 'lib/constants';
import { getBrandUISettings } from 'lib/utilities/Brands';
import imageSizes from 'styles/imgSizes';
import {
	ClientSideConfig,
	Cruise,
	CruiseSrc,
	DaySrc,
	Image,
	ImageSrc,
	Itinerary,
	ItinerarySrc,
	Location,
	PortSrc,
	PortVisitSrcActivity,
	Price,
	Sailing,
	SailingSrc,
	Ship,
	ShipSrc,
	StateroomClass,
	StateroomClassPriceSrc,
	StateroomClassSrc,
} from 'types';
import { MediaSrc } from 'types/MediaSrc';
import { cruisingLocationCodes } from './SearchContext.constants';

const reduceDays = (days: Array<DaySrc>): Array<Location> =>
	days
		?.reduce((ports: Location[], day: DaySrc) => {
			if (day.ports[0].activity !== PortVisitSrcActivity.Touring) {
				return [...ports, reducePort(day.ports[0].port)];
			}

			return [...ports];
		}, [])
		?.filter((port: Location) => !cruisingLocationCodes.includes(port.code)) ?? [];

const reducePort = ({ code, name, region }: PortSrc): Location => {
	const portRegion = region ? `, ${region}` : '';

	return {
		code,
		name: `${name}${portRegion}`,
	};
};

const reduceItinerary = (itinerary: ItinerarySrc, overviewImage: ImageSrc, productViewLink: string, config: ClientSideConfig): Itinerary => {
	const { code, days, departurePort, destination, name, ship, totalNights, preTour, postTour, portSequence } = itinerary;

	return {
		code,
		name,
		days,
		overviewImage: getOverviewImage(overviewImage, itinerary, config),
		destination,
		totalNights,
		ship: reduceShip(ship, config),
		departurePort: reducePort(departurePort),
		arrivalPort: getArrivalPort(days),
		visitingPorts: reduceDays(days),
		postTourCities: reduceTours(postTour?.days),
		preTourCities: reduceTours(preTour?.days),
		productViewLink: productViewLink,
		portSequence,
	};
};

export const reduceCruise = (cruiseSrc: CruiseSrc, config: ClientSideConfig, cabinClass: Array<string> = null): Cruise => {
	const { featuredImage, id: cruiseId, displaySailing: defaultLowestPriceSailing, masterSailing, productViewLink, sailings } = cruiseSrc;
	const lowestByCabin = cabinClass ? getLowestPriceByCabin(sailings, cabinClass) : defaultLowestPriceSailing;
	const lowestPriceSailing = lowestByCabin || defaultLowestPriceSailing;

	const itinerary = reduceItinerary(masterSailing.itinerary, featuredImage, productViewLink, config);

	return {
		id: cruiseId,
		itinerary,
		featuredSailing: reduceSailing(lowestPriceSailing),
		sailings: sailings.map((sailing) => reduceSailing(sailing)),
		// PENDING: Only to support legacy components - cruiseSrc should be removed once we refactor those components
		cruiseSrc,
	};
};

export const reducePrice = (stateroomClassPriceSrc: StateroomClassPriceSrc, taxesAndFees: number, hasTaxesAndFeesIncluded: boolean): Price => {
	return {
		netPrice: typeof stateroomClassPriceSrc.price?.value === 'number' ? stateroomClassPriceSrc.price?.value : null,
		taxesAndFees: taxesAndFees || 0,
		stateroomClassCode: stateroomClassPriceSrc.stateroomClass?.id || null,
		hasTaxesAndFeesIncluded: hasTaxesAndFeesIncluded || false,
	};
};

export const reduceSailing = (sailingSrc: SailingSrc): Sailing => {
	const lowestPrice: Price = sailingSrc.lowestStateroomClassPrice
		? reducePrice(sailingSrc.lowestStateroomClassPrice, sailingSrc.taxesAndFees?.value, sailingSrc.taxesAndFeesIncluded)
		: null;
	const pricing: Array<Price> = Array.isArray(sailingSrc.stateroomClassPricing)
		? sailingSrc.stateroomClassPricing.map((stateroomClassPrice) =>
			reducePrice(stateroomClassPrice, sailingSrc.taxesAndFees?.value, sailingSrc.taxesAndFeesIncluded),
		)
		: [];

	return {
		bookingLink: sailingSrc.bookingLink,
		id: sailingSrc.id,
		sailDate: sailingSrc.sailDate,
		startDate: sailingSrc.startDate,
		endDate: sailingSrc.endDate,
		lowestPrice,
		pricing,
	};
};

export const getOverviewImage = (overviewImage: ImageSrc, { name, ship }: ItinerarySrc, { brand, imageBaseUrl }: ClientSideConfig): Image => {
	const { defaultOverviewImagePath } = getBrandUISettings(brand.code);
	const path = overviewImage?.path || defaultOverviewImagePath;
	const {
		img: { medium },
	} = imageSizes[brand.code];
	const imagePath = `${imageBaseUrl}${path.replace(IMAGE_AEM_CLOUD_PREFIX, '')}?${medium}`;
	const imageAlt = `${ship?.name} - ${name}`;
	return {
		path: imagePath,
		altText: imageAlt,
	};
};

const getLowestPriceByCabin = (sailings: Array<SailingSrc>, cabinClass: Array<string>): SailingSrc => {
	const pricesByClass = sailings.map((group) => {
		const stateroomClassPricing = group.stateroomClassPricing.filter((item) => cabinClass?.includes(item?.stateroomClass?.id) && item?.price);
		const { id, endDate, sailDate, startDate, bookingLink, taxesAndFees, taxesAndFeesIncluded } = group;
		const cabinClassInfo = {
			id,
			endDate,
			sailDate,
			startDate,
			bookingLink,
			taxesAndFees,
			taxesAndFeesIncluded,
			lowestStateroomClassPrice: minBy(stateroomClassPricing, (item) => item?.price?.value),
			stateroomClassPricing: [],
		};
		return cabinClassInfo;
	});
	return minBy(pricesByClass, (item) => item?.lowestStateroomClassPrice?.price?.value);
};

const reduceShip = (ship: ShipSrc, config: ClientSideConfig): Ship => ({
	code: ship?.code || '',
	name: ship?.name || '',
	stateroomClasses: reduceStateroomClasses(ship?.stateroomClasses, config) || [],
});

const reduceTours = (days: Array<DaySrc>): Array<Location> =>
	days?.reduce(
		(cities: Array<Location>, day) => [...cities, reducePort(day.ports.find((port) => port.activity === PortVisitSrcActivity.Touring).port)],
		[],
	) ?? [];

const reduceStateroomClasses = (stateroomClasses: Array<StateroomClassSrc>, config: ClientSideConfig): Array<StateroomClass> =>
	stateroomClasses?.map((item) => ({
		id: item.id,
		code: item.content?.code,
		amenities: item.content?.amenities,
		maxCapacity: item.content?.maxCapacity,
		superCategory: item.name,
		image: getStateroomClassImage(item.content?.media, config),
	}));

const getStateroomClassImage = (mediaSrc: MediaSrc, { brand, imageBaseUrl }: ClientSideConfig): Image => {
	const image = mediaSrc?.images.find((item) => item);
	const {
		img: { medium },
	} = imageSizes[brand.code];
	const imagePath = image ? `${imageBaseUrl}${image.path?.replace(IMAGE_AEM_CLOUD_PREFIX, '')}?${medium}` : ' ';

	return {
		path: imagePath,
		altText: image?.meta.title || image?.meta?.description || image?.meta?.location || '',
	};
};

export const getArrivalPort = (days: Array<DaySrc>): Location =>
	reducePort(days?.flatMap((day) => day.ports)?.find((port) => port.activity === PortVisitSrcActivity.Debark).port);
